import {css} from 'styled-components';
import {tokensForThemeProvider} from '@forecast-it/design-system';

const {color, elevation} = tokensForThemeProvider.sky.sys;

export const FEATURE_FLAG_CARD_SURFACE_BACKGROUND = color.background.neutral['1'];
export const FEATURE_FLAG_CARD_BORDER = color.border.decorative;
export const FEATURE_FLAG_CARD_HOVER_BACKGROUND = color.background.neutral['2'];
export const FEATURE_FLAG_CARD_HOVER_BORDER = color.border.active;
export const FEATURE_FLAG_PROGRESS_FILL = color.background.primary['3'];

/**
 * Boundary for a hand-rolled input, matching what the design system gives its own text fields.
 *
 * Deliberately not the decorative border used by cards and table rules: that token is a light grey
 * meant to separate large surfaces, and at 1px around a small control it is too low-contrast to read
 * as a boundary at all — the field stops looking like something you can type into.
 */
export const FEATURE_FLAG_INPUT_BORDER = color.border.enabled;

/**
 * Text colour for a link that reads as a link — the back link out of a flag's detail page.
 *
 * Needed as a raw token because the typography components paint their own colour onto the element
 * they render, and `Detail` has no primary among the colours it accepts, so the colour has to be set
 * on the link's own rule instead of passed as a prop.
 */
export const FEATURE_FLAG_LINK_TEXT = color.text.content.primary;

/**
 * Light neutral fill for a strip or chip that needs to sit back from the card behind it: the
 * assignment table's column header, and the team pill in a flag's detail header. The palette's only
 * light neutral surface — its token name says "disabled" because that is where the design system
 * first used it, not because anything drawn on it is disabled.
 */
export const FEATURE_FLAG_NEUTRAL_FILL = color.background.disabled['1'];

/**
 * Unfilled portion of a rollout bar. The one value here with no semantic token behind it: the
 * palette has no neutral at this weight, and the bar needs a track darker than the card it sits on
 * but lighter than a border. Read by `FeatureFlagRolloutBar`, which is the single bar both screens
 * render, so the same gauge cannot end up drawn two different ways.
 */
export const FEATURE_FLAG_PROGRESS_TRACK = '#e6e6ed';

/**
 * Shadow cast over the bottom edge of a scrolling region to show there is more content below.
 *
 * A raw translucent value because the palette exposes only opaque colours and complete box-shadow
 * strings, and this one has to sit inside a gradient. Derived from the darkest neutral so it reads as
 * a shadow rather than a grey band.
 */
export const FEATURE_FLAG_SCROLL_SHADOW = 'rgba(35, 35, 52, 0.16)';

const FEATURE_FLAG_CARD_RADIUS = '4px';

/**
 * Chrome every card in this feature shares: the flag rows on the list, and the header, stat, and
 * table cards on a flag's detail page. Spread into a styled component, then add that component's own
 * layout and padding:
 *
 *     const Card = styled.div`
 *         ${featureFlagCardSurface}
 *         padding: 16px 20px;
 *     `;
 */
export const featureFlagCardSurface = css`
	background: ${FEATURE_FLAG_CARD_SURFACE_BACKGROUND};
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: ${FEATURE_FLAG_CARD_RADIUS};
	box-shadow: ${elevation.shadow['1']};
`;
